/**
 * What stage a ticket is at - decided once, for every screen that groups them.
 *
 * WHY THIS IS A FILE AND NOT A FILTER ON A PAGE. The contractor portal had six
 * buttons - All, Open, In Progress, On Hold, Resolved, Closed - and filtered on
 * `assignment.status === filter`. ticket_statuses holds SEVEN names: those
 * five plus "Awaiting Approval" and "Approved". So a contractor's ticket in
 * either of those appeared under All and under no button at all, and
 * "Approved" is the state a ticket takes the moment its hours are signed off -
 * which is precisely the work a contractor is waiting to be paid for. Gordon
 * asked for an Unbilled tab and asked to be told if anything was missing: two
 * statuses were, and one of them was the one he was asking about.
 *
 * The same shape of bug was found and fixed on the staff "My tickets" page
 * weeks earlier, and the portal never got the same treatment, because the
 * grouping lived on the page rather than anywhere both could read.
 *
 * A PARTITION, NOT A SET OF FILTERS. Gordon's call: every ticket belongs to
 * exactly one stage, so the counts on the tabs add up to the count on All and
 * a ticket cannot hide between two of them. `assertPartition` below is what
 * holds that down, and it is exported so a test can run it over every status
 * the database actually has rather than the ones somebody remembered.
 *
 * WHERE "UNBILLED" COMES FROM, and why there is no separate Resolved stage.
 * Gordon's definition is "anything finished and not invoiced". Invoicing
 * closes a ticket - routes/contractor-portal.js sets the status to Closed in
 * the same transaction that writes the invoice number - so a Resolved or
 * Approved ticket is ALWAYS uninvoiced, and a Resolved stage under a strict
 * partition would be a subset of Unbilled rather than something beside it.
 * Resolved and Approved therefore live in Unbilled, and Closed splits on
 * whether an invoice exists. A Closed ticket with no invoice lands in Unbilled
 * too, which is not a normal state and is meant to stand out: it is work
 * somebody closed without billing it.
 */

/** The stage a ticket is at. Exactly one of these, always. */
export const STAGES = [
  {
    key: 'awaiting',
    label: 'Awaiting approval',
    // Had no tab at all. A reopened ticket comes back here, so it is not
    // hypothetical.
    hint: 'Raised, waiting for somebody to agree the work'
  },
  { key: 'open', label: 'Open', hint: 'Agreed, not started' },
  { key: 'in_progress', label: 'In progress', hint: 'Being worked on' },
  { key: 'on_hold', label: 'On hold', hint: 'Waiting on something' },
  {
    key: 'unbilled',
    label: 'Unbilled',
    hint: 'Finished and not yet on an invoice'
  },
  { key: 'closed', label: 'Closed', hint: 'Invoiced and settled' }
];

export const STAGE_KEYS = STAGES.map((s) => s.key);

const norm = (v) => String(v || '').trim().toLowerCase();

/**
 * The invoice reference on a ticket, whatever the endpoint called it.
 *
 * The lists return `invoice_number`; the admin ticket search returns
 * `invoice_numbers` (comma-joined, because a reopened job can touch two). Both
 * are read, so a screen built on either is right.
 */
export const invoiceOf = (t) => {
  const one = t?.invoice_number;
  const many = t?.invoice_numbers;
  const v = (one !== undefined && one !== null && one !== '') ? one : many;
  return v === undefined || v === null || v === '' ? null : String(v);
};

/**
 * Which stage this ticket is at.
 *
 * Unknown or missing statuses fall to 'awaiting' rather than being dropped:
 * a ticket with a status nobody anticipated must still appear SOMEWHERE, and
 * the first tab is where somebody will look for a ticket that seems to have
 * gone missing. Silently returning null is how this bug happened the first
 * time.
 */
export function stageOf(ticket) {
  const status = norm(ticket?.status_name || ticket?.status);
  const invoiced = Boolean(invoiceOf(ticket));

  if (status === 'open') return 'open';
  if (status === 'in progress') return 'in_progress';
  if (status === 'on hold') return 'on_hold';
  // Finished as far as the contractor is concerned, and finished as far as the
  // firm is concerned. Neither can be invoiced - invoicing closes the ticket -
  // so both are money still owed.
  if (status === 'resolved' || status === 'approved') return 'unbilled';
  // The only status that can be either. Closed WITHOUT an invoice is work
  // somebody closed without billing it, which is worth seeing rather than
  // filing away with the settled jobs.
  if (status === 'closed') return invoiced ? 'closed' : 'unbilled';
  return 'awaiting';
}

/** Count per stage, plus the total, for the tab row. */
export function countByStage(tickets) {
  const counts = Object.fromEntries(STAGE_KEYS.map((k) => [k, 0]));
  for (const t of tickets || []) counts[stageOf(t)] += 1;
  counts.all = (tickets || []).length;
  return counts;
}

/** The tickets at one stage, or all of them for 'all'. */
export function atStage(tickets, stage) {
  if (!stage || stage === 'all') return tickets || [];
  return (tickets || []).filter((t) => stageOf(t) === stage);
}

/**
 * Does the grouping actually cover everything?
 *
 * Exported so a test can assert it over the real list of statuses rather than
 * a remembered one: give it every status name the database has and it reports
 * any that no stage claims, any claimed twice, and whether the per-stage counts
 * add up to the total. This is the check whose absence let two statuses fall
 * through six buttons.
 */
export function assertPartition(tickets) {
  const counts = countByStage(tickets);
  const summed = STAGE_KEYS.reduce((n, k) => n + counts[k], 0);
  const seen = new Map();
  for (const t of tickets || []) {
    const s = norm(t?.status_name || t?.status) || '(none)';
    if (!seen.has(s)) seen.set(s, new Set());
    seen.get(s).add(stageOf(t));
  }
  return {
    total: counts.all,
    summed,
    adds_up: summed === counts.all,
    // A status that lands in two different stages means the rule depends on
    // something other than the status - which is true of 'closed', and must be
    // true of nothing else.
    split: [...seen.entries()]
      .filter(([, set]) => set.size > 1)
      .map(([status, set]) => ({ status, stages: [...set] })),
    byStage: counts
  };
}

export default stageOf;
